import { DEFAULT_TENANT_ID } from "../../config/app.config";
import { isTenantSubdomain } from "../AppUtils";
import {
    SESSION_STORAGE_ASSUME_MODE,
    SESSION_STORAGE_TENANT_CONTEXT,
    clearCurrentTenantId,
    getCurrentTenantIdOrNull,
    isAssumeMode,
    setAssumeMode,
    setCurrentTenantId,
} from "../AuthUtils/TokenStore";

// TokenStore owns the per-tab routing keys; these are re-exported so NG-8055 callers/tests that key
// off the storage names keep one source of truth.
export const TENANT_CONTEXT_KEY = SESSION_STORAGE_TENANT_CONTEXT;
export const ASSUME_MODE_KEY = SESSION_STORAGE_ASSUME_MODE;
const CTX_PARAM = "ctx";
const ASSUME_PARAM = "assume";

const isValidTenantId = (value: unknown): value is number => typeof value === "number" && Number.isInteger(value) && value > 0;

// Navigation-layer aliases for NG-8055 callers. Each delegates to TokenStore so the sessionStorage
// access has a single owner — this module no longer touches sessionStorage for these keys directly.
export const getTenantContextFromSession = (): number | null => getCurrentTenantIdOrNull();

export const setTenantContextInSession = (tenantId: number): void => {
    if (!isValidTenantId(tenantId)) {
        return;
    }
    setCurrentTenantId(tenantId);
};

export const clearTenantContextFromSession = (): void => clearCurrentTenantId();

export const isAssumeModeFromSession = (): boolean => isAssumeMode();

export const setAssumeModeInSession = (assume: boolean): void => setAssumeMode(assume);

export interface BootstrapTenantContextResult {
    ctx: number | null;
    assume: boolean;
}

/**
 * Reads `?ctx=<tenantId>` and `?assume=1` from the current URL, writes both to sessionStorage,
 * then strips both params via history.replaceState (preserving other params + hash).
 * Must run before any authed API call. Safe to invoke multiple times — second call is a no-op.
 */
export const readAndScrubTenantContextFromUrl = (): BootstrapTenantContextResult => {
    if (typeof window === "undefined") {
        return { ctx: null, assume: false };
    }

    const url = new URL(window.location.href);
    const ctxRaw = url.searchParams.get(CTX_PARAM);
    const assumeRaw = url.searchParams.get(ASSUME_PARAM);

    let ctxValue: number | null = null;
    if (ctxRaw !== null) {
        const parsed = Number(ctxRaw);
        if (ctxRaw.trim() !== "" && isValidTenantId(parsed)) {
            ctxValue = parsed;
            setTenantContextInSession(parsed);
        }
        url.searchParams.delete(CTX_PARAM);
    }

    let assumeValue = false;
    if (assumeRaw !== null) {
        if (assumeRaw === "1") {
            assumeValue = true;
            setAssumeModeInSession(true);
        }
        url.searchParams.delete(ASSUME_PARAM);
    }

    if (ctxRaw !== null || assumeRaw !== null) {
        const newSearch = url.searchParams.toString();
        const queryString = newSearch ? `?${newSearch}` : "";
        const newUrl = `${url.pathname}${queryString}${url.hash}`;
        window.history.replaceState(window.history.state, "", newUrl);
    }

    return { ctx: ctxValue, assume: assumeValue };
};

/**
 * Seed the per-tab tenant context from the public tenant config when the app is served from a
 * tenant subdomain (e.g. acme.demo.1health.io) and the tab has no context yet.
 *
 * Why: the App Launcher token store keys tokens by tenant id, and the per-tab `tenantContext`
 * decides which slot is read. A fresh tab opened from a plain link (no `?ctx=`) falls back to
 * DEFAULT_TENANT_ID, which on a tenant subdomain can never match that origin's slot — so an
 * authenticated user is treated as logged out and forced through a full re-auth (and MFA).
 * On a tenant subdomain the host pins the tenant, making the config's tenantId authoritative.
 * The root domain is deliberately NOT seeded: there NG-8055's fallback chain
 * (sessionStorage → `/myself` → default) must keep running, since the host implies nothing.
 *
 * Call order matters: after `readAndScrubTenantContextFromUrl` (an explicit `?ctx=` must win) and
 * before the route tree renders — App gates rendering on the tenant-config response, so calling
 * this from its response handler guarantees auth checks see the seeded context.
 */
export const seedTenantContextFromSubdomainConfig = (configTenantId: number | null | undefined): void => {
    if (!isTenantSubdomain()) {
        return;
    }
    if (getCurrentTenantIdOrNull() !== null) {
        return;
    }
    if (!isValidTenantId(configTenantId)) {
        return;
    }
    setCurrentTenantId(configTenantId);
};

/**
 * Returns the tenant ID to use right now, following NG-8055's fallback chain:
 * sessionStorage → `/myself` user.tenantContext.id → DEFAULT_TENANT_ID.
 */
export const resolveCurrentTenantId = (myselfTenantId?: number | null): number => {
    const fromSession = getTenantContextFromSession();
    if (fromSession !== null) {
        return fromSession;
    }
    if (isValidTenantId(myselfTenantId)) {
        return myselfTenantId;
    }
    return DEFAULT_TENANT_ID;
};
